import { useState, useEffect } from 'react';
import { useDiagramStore } from '../../stores/diagramStore';
import { colorService } from '../../services/color';
import { rememberDrawColor } from '../../utils/colorPresets';
import type { Node, Edge, NodeGroup } from '../../types';

interface ColorPickerModalProps {
  targetNodeIds: string[];
  targetGroupIds?: string[];
  targetEdgeIds?: string[];
  targetParticipantIds?: string[];
  targetMessageIds?: string[];
  avoidRect?: { x: number; y: number; w: number; h: number };
  windowSize: { width: number; height: number };
  palette: {
    panelBg: string;
    panelBorder: string;
    panelText: string;
    panelSubText: string;
    edgeSelected: string;
  };
  snapshotBeforeMutation: () => void;
  onClose: () => void;
}

export function ColorPickerModal({
  targetNodeIds,
  targetGroupIds = [],
  targetEdgeIds = [],
  targetParticipantIds = [],
  targetMessageIds = [],
  avoidRect,
  windowSize,
  palette,
  snapshotBeforeMutation,
  onClose,
}: ColorPickerModalProps) {
  const { nodes, edges, groups, updateNode, updateEdge, updateGroup, participants, messages, updateParticipant, updateMessage } = useDiagramStore();

  const [pickerHue, setPickerHue] = useState(220);
  const [pickerSaturation, setPickerSaturation] = useState(75);
  const [pickerLightness, setPickerLightness] = useState(55);
  const [pickerHex, setPickerHex] = useState('#6366F1');
  const [recentColors, setRecentColors] = useState<string[]>(() => colorService.getRecentColors());

  const targetNodes = targetNodeIds.map(id => nodes.find(n => n.id === id)).filter(Boolean) as Node[];
  const targetGroups = targetGroupIds.map(id => groups.find(g => g.id === id)).filter(Boolean) as NodeGroup[];
  const targetEdgeObjs = targetEdgeIds.map(id => edges.find(e => e.id === id)).filter(Boolean) as Edge[];
  const targetParticipants = targetParticipantIds.map(id => participants.find(p => p.id === id)).filter(Boolean) as typeof participants;
  const targetMessageObjs = targetMessageIds.map(id => messages.find(m => m.id === id)).filter(Boolean) as typeof messages;

  useEffect(() => {
    const hex = targetParticipants.length > 0
      ? targetParticipants[0]?.color
      : targetMessageObjs.length > 0
        ? targetMessageObjs[0]?.color
        : targetGroups.length > 0
          ? targetGroups[0]?.color
          : targetEdgeObjs.length > 0
            ? targetEdgeObjs[0]?.color
            : targetNodes[0]?.data?.color;
    if (hex && colorService.isValidHex(hex)) {
      const [h, s, l] = colorService.hexToHsl(hex);
      setPickerHue(h);
      setPickerSaturation(s);
      setPickerLightness(l);
      setPickerHex(hex.toUpperCase());
    }
  }, []);

  if (targetNodes.length === 0 && targetGroups.length === 0 && targetEdgeObjs.length === 0
      && targetParticipants.length === 0 && targetMessageObjs.length === 0) {
    return null;
  }

  const label = targetParticipants.length > 0
    ? `"${targetParticipants[0].label}"`
    : targetMessageObjs.length > 0
      ? `"${targetMessageObjs[0].label || 'message'}"`
      : targetGroups.length > 0
        ? `"${targetGroups[0].label}"`
        : targetEdgeObjs.length > 0
          ? (targetEdgeObjs.length === 1 ? 'edge' : `${targetEdgeObjs.length} edges`)
          : (targetNodes.length === 1 ? `"${targetNodes[0].label}"` : `${targetNodes.length} nodes`);

  const previewColor = colorService.hslToHex(pickerHue, pickerSaturation, pickerLightness);

  const liveApply = (color: string) => {
    targetNodes.forEach(n => updateNode(n.id, { data: { ...n.data, color } }));
    targetGroups.forEach(g => updateGroup(g.id, { color }));
    targetEdgeObjs.forEach(ed => updateEdge(ed.id, { color }));
    targetParticipants.forEach(p => updateParticipant(p.id, { color }));
    targetMessageObjs.forEach(m => updateMessage(m.id, { color }));
  };

  const applyColor = (color: string) => {
    snapshotBeforeMutation();
    liveApply(color);
    rememberDrawColor(color);
    colorService.addRecentColor(color);
    setRecentColors(colorService.getRecentColors());
    onClose();
  };

  const syncFromHsl = (h: number, s: number, l: number) => {
    const hex = colorService.hslToHex(h, s, l);
    setPickerHex(hex);
    liveApply(hex);
  };

  const handleClose = () => {
    liveApply(previewColor);
    rememberDrawColor(previewColor);
    colorService.addRecentColor(previewColor);
    onClose();
  };

  // Smart positioning
  const MODAL_W = 360, MODAL_H = 480;
  const sw = windowSize.width, sh = windowSize.height;
  let modalTop: number | undefined;
  let modalTransform = 'translateX(-50%)';
  if (avoidRect) {
    const ar = avoidRect;
    const modalCY = sh / 2;
    const modalY1 = modalCY - MODAL_H / 2;
    const modalY2 = modalCY + MODAL_H / 2;
    const modalX1 = sw / 2 - MODAL_W / 2;
    const modalX2 = sw / 2 + MODAL_W / 2;
    const overlapX = modalX1 < ar.x + ar.w && modalX2 > ar.x;
    const overlapY = modalY1 < ar.y + ar.h && modalY2 > ar.y;
    if (overlapX && overlapY) {
      const spaceAbove = ar.y - 16;
      const spaceBelow = sh - (ar.y + ar.h) - 16;
      if (spaceAbove >= MODAL_H) modalTop = ar.y - MODAL_H - 8;
      else if (spaceBelow >= MODAL_H) modalTop = ar.y + ar.h + 8;
      else modalTop = spaceAbove > spaceBelow ? 16 : sh - MODAL_H - 16;
      modalTransform = 'translateX(-50%)';
    }
  }

  const hueOnlyColor = colorService.hslToHex(pickerHue, 100, 50);

  return (
    <div className="fixed inset-0 z-[240] flex items-center justify-center pointer-events-none">
      <div className="absolute inset-0 pointer-events-auto" onClick={handleClose} />
      <div
        className="relative z-[241] rounded-2xl shadow-2xl border p-5 pointer-events-auto"
        style={{
          width: 360,
          backgroundColor: palette.panelBg,
          borderColor: palette.panelBorder,
          ...(modalTop !== undefined ? { position: 'fixed', top: modalTop, left: '50%', transform: modalTransform } : {}),
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="font-semibold text-sm" style={{ color: palette.panelText }}>Color — {label}</div>
          <button
            onClick={handleClose}
            className="w-6 h-6 rounded-full flex items-center justify-center hover:bg-white/10 transition-colors"
            style={{ color: palette.panelSubText }}
          >
            ✕
          </button>
        </div>

        {/* Saturation / Brightness 2D square — HSV model: x=saturation, y=value */}
        <div
          className="relative w-full rounded-lg mb-3 cursor-crosshair select-none"
          style={{
            height: 180,
            background: `linear-gradient(to top, #000, transparent), linear-gradient(to right, #fff, ${hueOnlyColor})`,
          }}
          onMouseDown={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            const update = (ex: number, ey: number) => {
              // sv: saturation (x), value (y) — fully independent axes
              const sv = Math.max(0, Math.min(1, (ex - rect.left) / rect.width));
              const vv = Math.max(0, Math.min(1, 1 - (ey - rect.top) / rect.height));
              // Convert HSV → HSL for storage
              const l = vv * (1 - sv / 2);
              const s = l === 0 || l === 1 ? 0 : (vv - l) / Math.min(l, 1 - l);
              const lPct = Math.round(l * 100);
              const sPct = Math.round(s * 100);
              setPickerSaturation(sPct);
              setPickerLightness(Math.max(2, Math.min(98, lPct)));
              syncFromHsl(pickerHue, sPct, Math.max(2, Math.min(98, lPct)));
            };
            update(e.clientX, e.clientY);
            const onMove = (me: MouseEvent) => update(me.clientX, me.clientY);
            const onUp = () => {
              window.removeEventListener('mousemove', onMove);
              window.removeEventListener('mouseup', onUp);
            };
            window.addEventListener('mousemove', onMove);
            window.addEventListener('mouseup', onUp);
          }}
        >
          {(() => {
            // Convert stored HSL back to HSV for dot position
            const l = pickerLightness / 100;
            const s = pickerSaturation / 100;
            const v = l + s * Math.min(l, 1 - l);
            const sv = v === 0 ? 0 : 2 * (1 - l / v);
            return (
              <div
                className="absolute w-3 h-3 rounded-full border-2 border-white shadow pointer-events-none"
                style={{
                  left: `${sv * 100}%`,
                  top: `${(1 - v) * 100}%`,
                  transform: 'translate(-50%, -50%)',
                  backgroundColor: previewColor,
                  boxShadow: '0 0 0 1px rgba(0,0,0,0.4)',
                }}
              />
            );
          })()}
        </div>

        {/* Hue bar */}
        <div
          className="relative w-full rounded-lg mb-3 cursor-pointer select-none"
          style={{ height: 16, background: 'linear-gradient(to right, #ff0000, #ffff00, #00ff00, #00ffff, #0000ff, #ff00ff, #ff0000)' }}
          onMouseDown={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            const update = (ex: number) => {
              const h = Math.round(Math.max(0, Math.min(360, (ex - rect.left) / rect.width * 360)));
              setPickerHue(h);
              syncFromHsl(h, pickerSaturation, pickerLightness);
            };
            update(e.clientX);
            const onMove = (me: MouseEvent) => update(me.clientX);
            const onUp = () => {
              window.removeEventListener('mousemove', onMove);
              window.removeEventListener('mouseup', onUp);
            };
            window.addEventListener('mousemove', onMove);
            window.addEventListener('mouseup', onUp);
          }}
        >
          <div
            className="absolute top-0 bottom-0 w-3 rounded pointer-events-none"
            style={{
              left: `${pickerHue / 360 * 100}%`,
              transform: 'translateX(-50%)',
              border: '2px solid white',
              boxShadow: '0 0 0 1px rgba(0,0,0,0.4)',
            }}
          />
        </div>

        {/* Hex input + preview swatch */}
        <div className="flex items-center gap-2 mb-4">
          <div
            className="w-8 h-8 rounded-lg border flex-shrink-0"
            style={{ backgroundColor: previewColor, borderColor: palette.panelBorder }}
          />
          <input
            type="text"
            value={pickerHex}
            onChange={(e) => {
              const val = e.target.value;
              setPickerHex(val);
              if (colorService.isValidHex(val)) {
                const [h, s, l] = colorService.hexToHsl(val);
                setPickerHue(h);
                setPickerSaturation(s);
                setPickerLightness(l);
                liveApply(val.toUpperCase());
              }
            }}
            onBlur={() => {
              if (!colorService.isValidHex(pickerHex)) setPickerHex(previewColor);
            }}
            className="flex-1 px-3 py-1.5 rounded-lg text-sm font-mono border bg-transparent focus:outline-none focus:ring-1"
            style={{ borderColor: palette.panelBorder, color: palette.panelText }}
            placeholder="#RRGGBB"
            spellCheck={false}
          />
          <button
            onClick={() => applyColor(previewColor)}
            className="px-4 py-1.5 rounded-lg text-xs font-semibold transition-colors"
            style={{
              background: previewColor,
              color: colorService.lightness(previewColor) > 60 ? '#1e293b' : '#fff',
            }}
          >
            Apply
          </button>
        </div>

        {/* Recent colors */}
        {recentColors.length > 0 && (
          <div className="mb-3">
            <div className="text-xs mb-2" style={{ color: palette.panelSubText }}>Recent</div>
            <div className="flex flex-wrap gap-1.5">
              {recentColors.map((c, i) => (
                <button
                  key={i}
                  onClick={() => applyColor(c)}
                  className="w-6 h-6 rounded border-2 transition-transform hover:scale-110"
                  style={{
                    backgroundColor: c,
                    borderColor: previewColor.toLowerCase() === c.toLowerCase() ? '#fff' : 'transparent',
                  }}
                  title={c}
                />
              ))}
            </div>
          </div>
        )}

        {/* Preset swatches */}
        <div>
          <div className="text-xs mb-2" style={{ color: palette.panelSubText }}>Presets</div>
          <div className="grid grid-cols-10 gap-1">
            {[
              '#ffffff', '#e2e8f0', '#94a3b8', '#64748b', '#475569', '#334155', '#1e293b', '#0f172a', '#000000', '#6b7280',
              '#ef4444', '#f97316', '#f59e0b', '#eab308', '#84cc16', '#22c55e', '#10b981', '#14b8a6', '#06b6d4', '#0ea5e9',
              '#3b82f6', '#6366f1', '#8b5cf6', '#a855f7', '#d946ef', '#ec4899', '#f43f5e', '#dc2626', '#ea580c', '#d97706',
            ].map((c, i) => (
              <button
                key={i}
                onClick={() => applyColor(c)}
                className="w-6 h-6 rounded border transition-transform hover:scale-110"
                style={{
                  backgroundColor: c,
                  borderColor: previewColor.toLowerCase() === c.toLowerCase() ? '#fff' : `${palette.panelBorder}60`,
                }}
                title={c}
              />
            ))}
          </div>
        </div>

        <div className="text-xs mt-3" style={{ color: palette.panelSubText }}>
          Click square to pick · Apply or close to confirm · Esc to cancel
        </div>
      </div>
    </div>
  );
}
